import { useSelector } from "react-redux";
import { selectLocalChanges } from "../../app/slices/WebSocketSlice";
import { EnvironmentManagementService } from "../../services/EnvironmentManagementService";
import { selectToken } from "../../app/slices/AuthenticationSlice";
import { selectCurrentOrganization } from "../../app/slices/UserSlice";
import { useEffect, useState } from "react";
import { PropMetadata } from "../../dto/PropMetadata";
import { selectPropInteractions } from "../../app/slices/PropLibrarySlice";
import { PropManagementDTO } from "../../dto/PropManagementDTO";
import {
  ManagementPropType,
  PropManagementService,
} from "../../services/PropManagementService";
import { EnvironmentPropDTO } from "../../dto/EnvironmentPropDTO";

type SaveStatus = "pending" | "saving" | "success" | "error";

interface ItemStatus {
  status: SaveStatus;
  error?: string;
}

export default function useSaveLocalChanges() {
  const localChanges = useSelector(selectLocalChanges);
  const token = useSelector(selectToken);
  const org = useSelector(selectCurrentOrganization);
  const translatedInteractions = useSelector(selectPropInteractions);

  const [working, setWorking] = useState(false);
  const [itemStatuses, setItemStatuses] = useState<Map<string, ItemStatus>>(
    new Map()
  );

  // Initialize statuses when localChanges updates
  useEffect(() => {
    const initialStatuses = new Map<string, ItemStatus>();
    Object.values(localChanges).forEach((change) => {
      initialStatuses.set(change.id, { status: "pending" });
    });
    setItemStatuses(initialStatuses);
  }, [localChanges]);

  const saveLocalChanges = async () => {
    if (!token || !org) return false;

    // Set all items to 'saving'
    setItemStatuses((prev) => {
      const updated = new Map(prev);
      updated.forEach((value, key) => {
        updated.set(key, { status: "saving" });
      });
      return updated;
    });
    setWorking(true);

    // Save all changes
    const results = await Promise.allSettled([
      saveEnvironment(),
      saveEnvironmentProps(),
      ...saveAllPropsAndSituations(),
    ]);

    setWorking(false);

    return results.every(
      (result) => result.status === "fulfilled" && result.value === true
    );
  };

  const saveEnvironmentProps = async () => {
    if (!token || !org) return false;

    const environmentPropsChange = Object.values(localChanges).find(
      (lc) => lc.type === "EnvironmentProps"
    );

    if (!environmentPropsChange) return true;

    try {
      const environmentId = environmentPropsChange.id.replace(
        "environment-props:",
        ""
      );
      const environmentProps = JSON.parse(
        environmentPropsChange.metadata
      ) as EnvironmentPropDTO[];

      const result = await EnvironmentManagementService.SaveEnvironmentProps(
        org.id,
        token,
        environmentId,
        environmentProps
      );

      setItemStatuses((prev) =>
        new Map(prev).set(environmentPropsChange.id, {
          status: result ? "success" : "error",
          ...(result ? {} : { error: "Failed to save environment props" }),
        })
      );

      return result;
    } catch (error) {
      setItemStatuses((prev) =>
        new Map(prev).set(environmentPropsChange.id, {
          status: "error",
          error:
            error instanceof Error
              ? error.message
              : "Failed to save environment props",
        })
      );

      return false;
    }
  };

  // save environments
  // there can only be one environment change at a time
  const saveEnvironment = async () => {
    if (!token || !org) return false;

    const envChange = Object.values(localChanges).find(
      (lc) => lc.type === "Environment"
    );

    if (!envChange) return true;

    try {
      const result = await EnvironmentManagementService.SaveEnvironment(
        org.id,
        token,
        envChange.id,
        envChange.metadata
      );

      // Update status to success
      setItemStatuses((prev) =>
        new Map(prev).set(envChange.id, {
          status: "success",
        })
      );

      // TODO: remove from local changes if successful

      return result;
    } catch (error) {
      // Update status to error
      setItemStatuses((prev) =>
        new Map(prev).set(envChange.id, {
          status: "error",
          error: error instanceof Error ? error.message : "Failed to save",
        })
      );

      return false;
    }
  };

  const saveAllPropsAndSituations = () => {
    const propChanges = Object.values(localChanges).filter(
      (lc) => lc.type === "Prop" || lc.type === "Situation"
    );

    return propChanges.map((change) =>
      saveProp(change.metadata, change.type === "Prop" ? "props" : "situations")
    );
  };

  const saveProp = async (data: string, propType: "props" | "situations") => {
    if (!token || !org) return false;

    const propMetadata = JSON.parse(data) as PropMetadata;
    const managementPropType: ManagementPropType =
      propType === "props" ? 0 : 1;

    // === create or update prop (this contains name, categories ids, interaction ids) ===
    const interactionIds: string[] = [];
    propMetadata.dynamicInteractions?.forEach((interaction) => {
      const translatedInteraction = translatedInteractions.find(
        (ti) => ti.name === interaction.apiInteractionName
      );
      if (translatedInteraction) {
        interactionIds.push(translatedInteraction.id);
      }
    });
    const uniqueInteractionIds = [...new Set(interactionIds)]; // make sure they are unique

    const categories = propMetadata.categories;

    const dto: PropManagementDTO = {
      name: propMetadata.name,
      categories: categories,
      interactions: uniqueInteractionIds,
      hideInPropLibrary: propMetadata.hideInPropLibrary ?? false,
    };

    // check if situation or prop

    // 1. update the prop
    const result = await PropManagementService.UpdateProp(
      token,
      org.id,
      managementPropType,
      propMetadata.id,
      dto
    );

    if (!result) {
      setItemStatuses((prev) =>
        new Map(prev).set(propMetadata.id, {
          status: "error",
          error: "Failed to update prop",
        })
      );
      return false;
    }

    // === Update prop metadata ===
    const metadataResult = await PropManagementService.UpdatePropMetadata(
      token,
      org.id,
      managementPropType,
      propMetadata.id,
      propMetadata
    );

    if (!metadataResult) {
      setItemStatuses((prev) =>
        new Map(prev).set(propMetadata.id, {
          status: "error",
          error: "Failed to update prop metadata",
        })
      );
      return false;
    }

    setItemStatuses((prev) =>
      new Map(prev).set(propMetadata.id, {
        status: "success",
      })
    );

    return true;
  };

  return {
    saveLocalChanges,
    saveEnvironment,
    saveEnvironmentProps,
    working,
    itemStatuses,
  };
}
